import { Stack, MenuItem, TextField, InputAdornment, IconButton } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { usePDF } from 'react-to-pdf';
import dayjs from 'dayjs';

import { IOrderData } from '../types';
import { Header } from '@app/sections/header';
import { useTable } from '@app/components/table';
import { Strings } from '@app/constants';
import { FormProvider, Iconify, RHFTextField } from '@app/components';
import { OrderDetails } from './order-details';
import { getOrderDetails, updateOrderStatus } from '../slice';
import { useRequestState } from '@app/hooks';
import { selectOrderDetailState, selectUpdateOrderStatusState } from '../selectors';
import { InvoiceOrderDetails } from '@app/components/invoice';
import { isActiveSlot } from '@app/utils';

const OrderDetailsPage = () => {
  const tableProps = useTable();
  const dispatch = useDispatch();

  const handleBack = () => {
    window.history.back();
  };

  const { state } = useLocation();
  useEffect(() => {
    dispatch(getOrderDetails({ id: state.id }));
  }, [dispatch, state]);

  const { data, loading } = useRequestState({
    stateSelector: selectOrderDetailState,
    errorShown: true,
  });

  useRequestState({
    stateSelector: selectUpdateOrderStatusState,
    successMessageShown: true,
    errorShown: true,
  });

  const [selectedData, setSelectedData] = useState('');

  const methods = useForm<IOrderData>({
    defaultValues: {
      orderStatus: data?.orderStatus[data?.orderStatus?.length - 1]?.orderStatus ?? '',
    },
  });

  useEffect(() => {
    if (data) {
      methods.reset({
        orderStatus: data?.orderStatus[data?.orderStatus?.length - 1]?.orderStatus ?? '',
      });
      setSelectedData(data?.orderStatus[data?.orderStatus?.length - 1]?.orderStatus ?? '');
    }
  }, [data, methods]);

  const { toPDF, targetRef } = usePDF({ filename: 'Order-Details.pdf' });
  const handleInvoice = () => {
    toPDF();
  };

  const [isPreview, setIsPreview] = useState(false);

  const handlePreview = () => {
    setIsPreview(!isPreview);
  };

  const handleBackToDetails = () => {
    setIsPreview(!isPreview);
  };

  const activeSlot = isActiveSlot(data?.orderSlots);

  const deliverySlot = activeSlot
    ? `${dayjs(activeSlot?.deliveryDate)
        // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
        .format('YYYY-MM-DD')}, ${activeSlot?.name}`
    : data?.estimatedDeliveryDate
      ? `Est. ${dayjs(data.estimatedDeliveryDate).format('YYYY-MM-DD')}`
      : 'Nationwide delivery';

  return (
    <Stack sx={{ overflow: 'auto', height: '100%' }}>
      <Header
        backButton={!isPreview}
        title={isPreview ? 'Invoice Preview' : Strings.pageTitle.orderManagement}
        onClickBack={() => {
          handleBack();
        }}
        onClickActionButtonImport={!isPreview ? handlePreview : undefined}
        actionButtonImport={!isPreview ? 'View Invoice' : undefined}
        actionButtonTitle={isPreview ? 'Download' : undefined}
        onClickActionButton={isPreview ? handleInvoice : undefined}
        additionalButtonTitle={isPreview ? 'Back' : undefined}
        onClickAdditionalButton={isPreview ? handleBackToDetails : undefined}
      />
      <Stack>
        <FormProvider methods={methods}>
          <Stack
            flexDirection="row"
            ml={3}
            mb={3}
            width="60%"
            gap={2.5}
            sx={{ display: isPreview ? 'none' : 'visible' }}
          >
            <RHFTextField
              name="orderStatus"
              variant="outlined"
              label={Strings.detailCard.type}
              select
              sx={{ width: '30%' }}
              onChange={(event) => {
                const selectedValue = event.target.value;
                methods.setValue('orderStatus', selectedValue);
                setSelectedData(selectedValue);
                dispatch(updateOrderStatus({ orderStatus: selectedValue, id: state.id }));
              }}
            >
              {['Confirmed', 'Preparing', 'Picked', 'Deliverying', 'Delivered', 'Canceled'].map(
                (value, index, array) => (
                  <MenuItem
                    key={value}
                    value={value}
                    disabled={
                      (selectedData === 'Delivered' && value === 'Canceled') ||
                      index <= array.indexOf(selectedData)
                    }
                  >
                    {value}
                  </MenuItem>
                )
              )}
            </RHFTextField>

            <TextField
              label="Estimated delivery"
              value={deliverySlot}
              disabled
              helperText={
                activeSlot
                  ? 'Slot changes are disabled (nationwide delivery)'
                  : data?.estimatedDeliveryDate
                    ? 'Order date + admin delivery days'
                    : 'No delivery estimate set'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton tabIndex={-1} disabled>
                      <Iconify icon="carbon:delivery" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ width: '65%' }}
            />
          </Stack>
        </FormProvider>
        <Stack ref={targetRef} display={isPreview ? 'visible' : 'none'}>
          <InvoiceOrderDetails details={data} />
        </Stack>
        <Stack display={isPreview ? 'none' : 'visible'}>
          <OrderDetails data={data} tableProps={tableProps} loading={loading} />
        </Stack>
      </Stack>
    </Stack>
  );
};
export { OrderDetailsPage };
